// Registry mapping `ui_kind` strings (declared on `ToolImpl.ui_kind` server-side)
// to the Vue component that renders that tool result inline in the chat thread.
//
// Adding a new card:
//   1. Set `ui_kind="<kind>"` on the tool's `ToolImpl` registration.
//   2. Build the card component under `cards/`.
//   3. Register `<kind>: Component` here.
//
// Components receive `{ result: unknown; toolName: string }`. They are
// responsible for casting `result` to the shape they expect (defined in
// `types.ts`). When `ui_kind` is unset or unregistered, `ToolResultRow.vue`
// falls back to its JSON debug view.

import type { Component } from "vue";

import GoalsListCard from "./cards/GoalsListCard.vue";
import PeopleTagsCard from "./cards/PeopleTagsCard.vue";
import PromoteCalendarIntegrationCard from "./cards/promotions/PromoteCalendarIntegrationCard.vue";
import PromoteConnectionCreationCard from "./cards/promotions/PromoteConnectionCreationCard.vue";
import PromoteFeedbackRequestCard from "./cards/promotions/PromoteFeedbackRequestCard.vue";
import PromoteGoalCreationCard from "./cards/promotions/PromoteGoalCreationCard.vue";
import PromoteRolePlayCard from "./cards/promotions/PromoteRolePlayCard.vue";
import RolePlaySetupCard from "./cards/RolePlaySetupCard.vue";
import RolePlayStartedCard from "./cards/RolePlayStartedCard.vue";
import RolePlaySummaryCard from "./cards/RolePlaySummaryCard.vue";

export const toolUiRegistry: Record<string, Component> = {
    goals_list: GoalsListCard,
    // people_tags renders inside the user's bubble, not as its own row. The
    // registry entry exists so ToolResultRow.vue's `cardComponent` lookup
    // returns truthy and the debug-JSON fallback stays hidden.
    people_tags: PeopleTagsCard,
    promote_calendar_integration: PromoteCalendarIntegrationCard,
    promote_connection_creation: PromoteConnectionCreationCard,
    promote_feedback_request: PromoteFeedbackRequestCard,
    promote_goal_creation: PromoteGoalCreationCard,
    promote_role_play: PromoteRolePlayCard,
    role_play_setup: RolePlaySetupCard,
    role_play_started: RolePlayStartedCard,
    role_play_summary: RolePlaySummaryCard,
};

// ui_kinds whose render lives elsewhere in the chat layout — Nadia2Chat.vue
// skips its <ToolResultRow> for these so we don't get a stray empty
// component slot in the thread.
export const INLINE_UI_KINDS: ReadonlySet<string> = new Set(["people_tags"]);

export const getToolUiComponent = (uiKind: string | null | undefined): Component | null => {
    if (!uiKind) {
        return null;
    }
    return toolUiRegistry[uiKind] ?? null;
};
